import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const HeroSection = () => {
  const [currentLanguage, setCurrentLanguage] = useState('en');

  useEffect(() => {
    const savedLanguage = localStorage.getItem('memorial-language') || 'en';
    setCurrentLanguage(savedLanguage);
    document.documentElement.dir = savedLanguage === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = savedLanguage;

    // Listen for language changes
    const handleLanguageChange = (event) => {
      const newLanguage = event.detail?.language || 'en';
      setCurrentLanguage(newLanguage);
      document.documentElement.dir = newLanguage === 'ar' ? 'rtl' : 'ltr';
      document.documentElement.lang = newLanguage;
    };

    window.addEventListener('languageChanged', handleLanguageChange);
    return () => window.removeEventListener('languageChanged', handleLanguageChange);
  }, []);

  const heroContent = {
    ar: {
      title: "عامٌ على النصر",
      date: "٨ ديسمبر ٢٠٢٤",
      location: "إسطنبول",
      subtitle: "في الذكرى الأولى ليومٍ غيّر الوعي السوري، نجتمع لنستعيد اللحظة التي أعادت للوطن صوته، وللناس انتماءهم.",
      cta: "سجِّل الآن للمشاركة في الفعالية",
      scrollText: "اكتشف المزيد"
    },
    en: {
      title: "A Year of Victory",
      date: "December 8, 2024",
      location: "Istanbul",
      subtitle: "On the first anniversary of a day that changed Syrian consciousness, we gather to reclaim the moment that restored the homeland's voice and people's sense of belonging.",
      cta: "Register Now to Participate in the Event",
      scrollText: "Discover More"
    },
    tr: {
      title: "Zaferin Bir Yılı",
      date: "8 Aralık 2024",
      location: "İstanbul",
      subtitle: "Suriye bilincini değiştiren bir günün ilk yıldönümünde, vatanın sesini ve halkın aidiyet duygusunu geri getiren anı yeniden kazanmak için bir araya geliyoruz.",
      cta: "Etkinliğe Katılmak İçin Şimdi Kayıt Olun",
      scrollText: "Daha Fazla Keşfet"
    }
  };

  const scrollToWhySection = () => {
    const element = document.getElementById('why-we-hold-event');
    if (element) {
      element?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToEngagement = () => {
    const element = document.getElementById('get-involved');
    if (element) {
      element?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const content = heroContent?.[currentLanguage];

  return (
    <section className="relative min-h-screen flex items-center justify-center bg-gradient-to-b from-[#006e4c]/5 via-[#006e4c]/3 to-background overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-transparent"></div>
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-accent/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-secondary/10 rounded-full blur-3xl"></div>
      </div>
      
      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="space-y-8"
        >
          {/* Memorial Title */}
          <div className="space-y-4">
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="font-memorial text-4xl md:text-6xl lg:text-7xl font-bold text-primary leading-tight"
            >
              {content?.title}
            </motion.h1>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="flex flex-col sm:flex-row items-center justify-center gap-4"
            >
              <div className="inline-flex items-center space-x-2 bg-card border border-border rounded-lg px-6 py-3 shadow-memorial">
                <Icon name="Calendar" size={20} className="text-accent" />
                <span className="font-body text-lg md:text-xl font-medium text-text-primary">
                  {content?.date}
                </span>
              </div>
              <div className="inline-flex items-center space-x-2 bg-card border border-border rounded-lg px-6 py-3 shadow-memorial">
                <Icon name="MapPin" size={20} className="text-accent" />
                <span className="font-body text-lg md:text-xl font-medium text-text-primary">
                  {content?.location}
                </span>
              </div>
            </motion.div>
          </div>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="font-body text-xl md:text-2xl text-text-secondary max-w-3xl mx-auto leading-relaxed"
          >
            {content?.subtitle}
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.0 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-8 pb-24"
          >
            <Button
              variant="default"
              size="lg"
              onClick={scrollToEngagement}
              iconName="Heart"
              iconPosition="left"
              className="w-full sm:w-auto hero-cta-button"
            >
              {content?.cta}
            </Button>
            
            {/* <Button
              variant="outline"
              size="lg"
              onClick={scrollToMemorialStory}
              iconName="ChevronDown"
              iconPosition="right"
              className="w-full sm:w-auto"
            >
              {content?.scrollText}
            </Button> */}
          </motion.div>
        </motion.div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.5 }}
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10"
        >
          <motion.button
            onClick={scrollToWhySection}
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            className="flex flex-col items-center space-y-2 text-text-secondary hover:text-primary memorial-transition"
          >
            <Icon name="ChevronDown" size={24} />
            <span className="font-body text-sm">{content?.scrollText}</span>
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default HeroSection;